import React, { Component } from 'react';
import {
    Container,
    Card,
    CardHeader,
    Typography,
    CardContent,
    Grid,
} from '@material-ui/core';
import { Spinner, Table } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { formatDate } from '../../Utils/formatDate';
import { AuthAPI, NewsLetterAPI } from '../../API';
import { PageStatus } from 'enums';
import { Show } from 'Layout';
import './MyRewards.css';
import { decryptTkData } from 'Helper/EncryptionDecryption';


const PAGE_SIZE = 50;

class MyRewards extends Component<any, any> {
    constructor(props) {
        super(props);
        this.state = {
            survey: [],
            total: 0,
            totalPartial: {
                "Referral": 0,
                "Survey": 0
            },
            baristaDetails: {
                rewardCorporation: "",
                rewardCoupon: ""
            },
            status: PageStatus.None,
            error: null,
            currentPage: 1,
            hasMore: false,
        };
    }

    componentDidMount() {
        if (this.props.userId) {
            this.fetchSurvey(this.props.userId, 1);
            this.fetchBaristaCoupon(this.props.userId);
        }
    }

    fetchSurvey = (userId, page: number) => {
        Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => AuthAPI.getAllByUserId(PAGE_SIZE, userId, page))
            .then((surveyData: any) => {
                if (surveyData) {
                    const data = surveyData.data || [];
                    const totalPointsByType = data.reduce((acc, reward) => {
                        const { points, rewardType } = reward;
                        acc[rewardType] = (acc[rewardType] || 0) + points;
                        return acc;
                    }, {});
                    this.setState({
                        survey: data,
                        total: surveyData.totalPoints,
                        totalPartial: totalPointsByType,
                        status: PageStatus.Loaded,
                        currentPage: page,
                        hasMore: data.length === PAGE_SIZE,
                    });
                }
            })
            .catch((err) => {
                this.setState({ error: err.message, status: PageStatus.Error });
            });
    };

    fetchBaristaCoupon = (userId) => {
        Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => NewsLetterAPI.getAllRewardsByUserId({ rewardAssoicatedUserId: userId }))
            .then((res) => {
                if (res?.data.length > 0) {
                    this.setState({
                        baristaDetails: {
                            rewardCorporation: res?.data[0]?.rewardCorporation,
                            rewardCoupon: res?.data[0]?.rewardCoupon,
                            expiryAt: res?.data[0]?.expiryAt,
                        }
                    })
                }
                this.setState({ status: PageStatus.Loaded });
            })
    }

    // Localize the reward-type column. The API sends literal English strings
    // ("Referral", "Survey", "Profile Completed", "Sign-in Bonus"); map known
    // types to existing i18n keys and fall back to the raw value for any
    // unknown type so the cell never breaks.
    localizeRewardType = (rewardType) => {
        const { t } = this.props;
        const keyByType = {
            'Referral': 'rewards.rewardTypeReferral',
            'Survey': 'rewards.rewardTypeSurvey',
            'Profile Completed': 'rewards.profileCompletedLabel',
        };
        const key = keyByType[rewardType];
        return key ? t(key, rewardType) : rewardType;
    };

    render() {
        const { survey, status, baristaDetails } = this.state;
        const { t } = this.props;
        const rewardObj = {
            en: {Barista: "Barista Coupon", ChaiPoint: "ChaiPoint Coupon", ExpiryDate: "Expiry Date"},
            hi: {Barista: "बारिस्ता कूपन", ChaiPoint: "चाईपॉइंट कूपन", ExpiryDate: "मान्यता की अंतिम तिथि"},
            // jp: English placeholders — copy of `en`. Translators replace values later.
            jp: {Barista: "Barista Coupon", ChaiPoint: "ChaiPoint Coupon", ExpiryDate: "Expiry Date"}
        }
        return (
            <div>
                <Container>
                    <Card className="mainCard">
                        <CardHeader
                            title={t('rewards.pointsEarnedHeading')}
                            titleTypographyProps={{ className: 'cardHeader' }}
                        />
                    </Card>
                    <Card className="nestedCard">
                        <CardContent>

                            <Grid container spacing={2} className="gridContainer">

                                <Grid item xs={6} sm={4} className="gridItem">
                                    <Typography variant="h4">{this.state.total ? this.state.total : 0}</Typography>
                                    <Typography variant="body1">{t('rewards.totalPointsEarnedLabel')}</Typography>
                                </Grid>


                                <Grid item xs={6} sm={4} className="gridItem">
                                    <Typography variant="h4">{this.state.totalPartial.Survey ? this.state.totalPartial.Survey : 0}</Typography>
                                    <Typography variant="body1">{t('rewards.earnedViaSurveysLabel')}</Typography>
                                </Grid>

                                <Grid item xs={6} sm={4} className="gridItem">
                                    <Typography variant="h4">{this.state.totalPartial.Referral ? this.state.totalPartial.Referral : 0}</Typography>
                                    <Typography variant="body1">{t('rewards.earnedViaReferralsLabel')}</Typography>
                                </Grid>

                            </Grid>
                        </CardContent>
                        {baristaDetails.rewardCorporation && baristaDetails.rewardCoupon && <div className='bg-gray rounded p-3 mx-2 text-white'>
                            <div className='d-flex justify-content-between align-items-center'>
                                <div>{rewardObj[this.props.language || 'en'][baristaDetails.rewardCorporation]}: {decryptTkData(baristaDetails.rewardCoupon)}</div>
                                <div>{rewardObj[this.props.language || 'en'].ExpiryDate}: {formatDate(baristaDetails.expiryAt, 'date')}</div>
                            </div>
                        </div>}
                    </Card>

                    <Card className="nestedCard">
                        <Show when={status === PageStatus.Loading}>
                            <div className="d-flex justify-content-center w-100 p-5">
                                <Spinner animation="border" variant="primary" />
                            </div>
                        </Show>

                        <Show when={status === PageStatus.Loaded}>
                            <CardHeader
                                title={t('rewards.rewardSummaryHeading')}
                                className="cardHeader"
                                titleTypographyProps={{ className: 'cardHeader' }}
                            />

                            <Show when={!this.state.survey.length}>
                                <CardContent>
                                    <Typography variant="body1">{t('rewards.noRewardSummaryEmptyText')}</Typography>
                                </CardContent>
                            </Show>
                        </Show>

                        <Show when={status === PageStatus.Loaded}>
                            <Show when={!!survey.length}>
                                <div className='table-responsive'>
                                    <Table responsive size="sm" bordered>
                                        <thead>
                                            <tr>
                                                <th>{t('common.columnSerialNo')}</th>
                                                <th>{t('rewards.tableColRewardDate')}</th>
                                                <th>{t('rewards.tableColType')}</th>
                                                <th>{t('common.columnPoints')}</th>
                                                <th>{t('rewards.tableColUser')}</th>
                                                <th>{t('rewards.tableColReferredUser')}</th>
                                                <th>{t('rewards.rewardTypeSurvey')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {survey.map((redemption, index) => (
                                                <tr key={"redemption" + index}>
                                                    <td>{(this.state.currentPage - 1) * PAGE_SIZE + index + 1}</td>
                                                    <td>
                                                        {redemption && redemption.rewardDate
                                                            ? formatDate(redemption.rewardDate)
                                                            : '-'}
                                                    </td>
                                                    <td>{this.localizeRewardType(redemption.rewardType)}</td>
                                                    <td>{redemption.points}</td>
                                                    <td>
                                                        {redemption.user ? redemption.user.email : '-'}
                                                    </td>
                                                    <td>
                                                        {/* referredUser = the invitee, joined on
                                                            rewards.referralId by the getAllByUserId
                                                            endpoint. Only present on Referral rows. */}
                                                        {redemption.referredUser
                                                            ? redemption.referredUser.email
                                                            : '-'}
                                                    </td>
                                                    <td>
                                                        {redemption.survey
                                                            ? redemption.survey.name
                                                            : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            disabled={this.state.currentPage <= 1}
                                            onClick={() => this.fetchSurvey(this.props.userId, this.state.currentPage - 1)}
                                        >
                                            {t('common.previousButton')}
                                        </button>
                                        <span>{t('common.paginationPageLabel', { page: this.state.currentPage })}</span>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            disabled={!this.state.hasMore}
                                            onClick={() => this.fetchSurvey(this.props.userId, this.state.currentPage + 1)}
                                        >
                                            {t('common.nextButton')}
                                        </button>
                                    </div>
                                </div>
                            </Show>
                        </Show>

                    </Card>
                </Container>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        userId: state.adminUser.adminUser.userId,
        role: state.adminUser.adminUser.role,
        phoneNumber: state.adminUser.adminUser.phoneNumber,
        email: state.adminUser.adminUser.email,
        isLoading: state.adminUser.adminUser.loading,
        language: state.adminUser.adminUser.language,
        error: state.adminUser.adminUser.error,
    };
};

export default connect(mapStateToProps)(withTranslation()(MyRewards));
